import { useCallback } from 'react';
import {
    BusinessDocType,
    backendServices,
    isFeatureEnabled,
    usePostDocument,
    usePostDocumentDraft,
    usePostDocumentDraftAttachments,
    usePutDocument,
} from 'common';
import { DOCUMENT_STATUS, mergeSimDocumentForSubmit } from './_shared/simDocumentVM';

function formatApiError(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: unknown; status?: number } }).response;
        const data = response?.data;
        if (Array.isArray(data)) {
            const messages = data
                .map((item) => (item && typeof item === 'object' && 'Message' in item ? String(item.Message) : null))
                .filter(Boolean);
            if (messages.length > 0) {
                return messages.join(' ');
            }
        }
        if (typeof data === 'string' && data.length > 0) {
            return data;
        }
        if (response?.status != null) {
            return `Request failed (${response.status}).`;
        }
    }
    return err instanceof Error ? err.message : 'Submission failed.';
}

/** Per-doc progress carried across retries: which draft to reuse, and whether its files already landed. */
export interface SimSubmitProgress {
    draftId?: number;
    attachmentsUploaded?: boolean;
}

/** `sent: false` = saved as a draft but not submitted; `failedStage` says why, for the retry message. */
export interface SimSubmitResult {
    draftId: number;
    attachmentsUploaded: boolean;
    sent: boolean;
    failedStage?: 'attachments' | 'submit';
    sendError?: unknown;
}

/** Default: POST /Document/Draft → attachments → GET → PUT Inserted. `PostSignupSimDirectPost` uses single POST /Document. */
export function useSubmitSimDocument() {
    const draftMutation = usePostDocumentDraft();
    const putMutation = usePutDocument();
    const postMutation = usePostDocument();
    const attachmentsMutation = usePostDocumentDraftAttachments();

    const submit = useCallback(
        async (
            body: backendServices.ViewModels.DocumentViewModel,
            progress: SimSubmitProgress = {},
            files?: File[]
        ): Promise<SimSubmitResult> => {
            // No draft to attach to on this path, so `files` are dropped. Attachments
            // only work on the default draft → PUT flow.
            if (isFeatureEnabled('PostSignupSimDirectPost')) {
                const directBody = {
                    ...body,
                    CommonFields: {
                        ...body.CommonFields,
                        Status: DOCUMENT_STATUS.Inserted,
                        Status_US: 'Inserted',
                    },
                } as backendServices.ViewModels.DocumentViewModel;
                try {
                    const created = (await postMutation.mutateAsync(directBody)) as unknown as {
                        ID?: number;
                        Id?: number | null;
                    };
                    return { draftId: created.ID ?? created.Id ?? 0, attachmentsUploaded: false, sent: true };
                } catch (err: unknown) {
                    throw new Error(`Submission failed: ${formatApiError(err)}`);
                }
            }

            let draftId = progress.draftId;
            if (draftId == null) {
                try {
                    const draft = await draftMutation.mutateAsync(body);
                    draftId = draft.ID;
                } catch (err: unknown) {
                    const message = formatApiError(err);
                    throw new Error(`Draft save failed: ${message}`);
                }
            }

            const resolvedDraftId = draftId as number;

            // Re-uploading duplicates rows, so this is gated on whether the files landed.
            let attachmentsUploaded = progress.attachmentsUploaded ?? false;
            if (!attachmentsUploaded && files != null && files.length > 0) {
                try {
                    await attachmentsMutation.mutateAsync({
                        documentId: resolvedDraftId,
                        businessDocType: BusinessDocType.SIMDocument,
                        files,
                    });
                    attachmentsUploaded = true;
                } catch (err: unknown) {
                    // Don't submit without the attachments; keep the draft so retry reuses it.
                    return {
                        draftId: resolvedDraftId,
                        attachmentsUploaded: false,
                        sent: false,
                        failedStage: 'attachments',
                        sendError: err,
                    };
                }
            }

            const api = new backendServices.Apis.DocumentApi();

            try {
                const { data: existingDoc } = await api.getDocument(resolvedDraftId);
                const sendBody = mergeSimDocumentForSubmit(
                    existingDoc as backendServices.ViewModels.DocumentViewModel,
                    resolvedDraftId
                );
                await putMutation.mutateAsync({ id: resolvedDraftId, body: sendBody });

                return { draftId: resolvedDraftId, attachmentsUploaded, sent: true };
            } catch (err: unknown) {
                return {
                    draftId: resolvedDraftId,
                    attachmentsUploaded,
                    sent: false,
                    failedStage: 'submit',
                    sendError: err,
                };
            }
        },
        [attachmentsMutation, draftMutation, postMutation, putMutation]
    );

    return {
        submit,
        isLoading:
            draftMutation.isLoading || attachmentsMutation.isLoading || putMutation.isLoading || postMutation.isLoading,
        isError: draftMutation.isError || attachmentsMutation.isError || postMutation.isError,
    };
}
